import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { getPlanningSettings, bulkUpdatePlanningSettings } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { FloppyDisk, ArrowsClockwise, MagnifyingGlass } from '@phosphor-icons/react';


export default function PlanningSettings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState({}); // sku_id → {field: value}
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await getPlanningSettings();
      setRows(r.data.rows || []);
      setDirty({});
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.toLowerCase().trim();
    return needle
      ? rows.filter(r => (r.sku_code || '').toLowerCase().includes(needle) || (r.sku_name || '').toLowerCase().includes(needle))
      : rows;
  }, [rows, q]);

  const patch = (sku_id, field, value) => {
    const num = value === '' ? null : Number(value);
    setDirty((d) => ({ ...d, [sku_id]: { ...(d[sku_id] || {}), [field]: num } }));
  };

  const val = (r, field) => {
    const patched = dirty[r.sku_id];
    if (patched && field in patched) return patched[field];
    return r[field];
  };

  const dirtyRows = useMemo(() =>
    Object.entries(dirty).map(([sku_id, p]) => ({ sku_id, ...p })), [dirty]);

  const handleSave = async () => {
    if (dirtyRows.length === 0) {
      toast.info('No changes to save');
      return;
    }
    setSaving(true);
    try {
      const { data } = await bulkUpdatePlanningSettings(dirtyRows);
      if ((data.errors || []).length > 0) {
        toast.warning(`${data.modified} saved, ${data.errors.length} errors`);
      } else {
        toast.success(`${data.modified} SKUs updated`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6" data-testid="planning-settings-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Planning Settings</h1>
            <p className="mt-2 text-sm opacity-70 max-w-3xl">
              Per-SKU lead-time, safety-stock, reorder-point, and minimum-order-quantity.
              These drive the <span className="font-mono">Suggested Reorders</span> and
              <span className="font-mono"> Shortage Report</span> math.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {dirtyRows.length > 0 && (
              <span className="text-[11px] font-mono opacity-60" data-testid="ps-dirty-count">
                {dirtyRows.length} unsaved
              </span>
            )}
            <Button variant="outline" size="sm" onClick={load} disabled={saving}>
              <ArrowsClockwise className="size-4" />
              <span className="ml-1.5">Reload</span>
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving || dirtyRows.length === 0} data-testid="ps-save-btn">
              <FloppyDisk className="size-4" />
              <span className="ml-1.5">Save {dirtyRows.length > 0 ? `(${dirtyRows.length})` : ''}</span>
            </Button>
          </div>
        </div>

        <Card className="p-3 flex items-center gap-2">
          <MagnifyingGlass className="size-4 opacity-50 ml-1" />
          <input type="text" placeholder="Search SKU code or name…"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 className="h-8 w-full px-2 text-[13px] bg-transparent border-0 outline-none"
                 data-testid="ps-search" />
        </Card>

        {loading ? (
          <div className="text-sm opacity-60">Loading settings…</div>
        ) : (
          <Card className="overflow-auto max-h-[75vh]">
            <table className="w-full text-[12px]" data-testid="planning-settings-table">
              <thead className="bg-muted/40 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">SKU</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-right px-3 py-2">Reorder Point</th>
                  <th className="text-right px-3 py-2">Reorder Max</th>
                  <th className="text-right px-3 py-2">Lead Time (days)</th>
                  <th className="text-right px-3 py-2">Safety Stock (days)</th>
                  <th className="text-right px-3 py-2">MOQ</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const isDirty = !!dirty[r.sku_id];
                  return (
                    <tr key={r.sku_id} className={`border-t border-border/40 ${isDirty ? 'bg-amber-500/5' : ''}`} data-testid={`ps-row-${r.sku_code}`}>
                      <td className="px-3 py-2 font-mono">{r.sku_code}</td>
                      <td className="px-3 py-2 opacity-80 truncate max-w-[200px]">{r.sku_name}</td>
                      <td className="px-3 py-2">
                        <input type="number" min="0" value={val(r, 'reorder_point') ?? ''}
                               onChange={(e) => patch(r.sku_id, 'reorder_point', e.target.value)}
                               className="h-7 w-20 px-2 text-right text-[12px] bg-background border border-border rounded font-mono" />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" min="0" value={val(r, 'reorder_max') ?? ''}
                               placeholder="—"
                               onChange={(e) => patch(r.sku_id, 'reorder_max', e.target.value)}
                               className="h-7 w-20 px-2 text-right text-[12px] bg-background border border-border rounded font-mono"
                               data-testid={`ps-row-${r.sku_code}-reorder-max`} />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" min="0" max="365" value={val(r, 'lead_time_days') ?? ''}
                               onChange={(e) => patch(r.sku_id, 'lead_time_days', e.target.value)}
                               className="h-7 w-20 px-2 text-right text-[12px] bg-background border border-border rounded font-mono" />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" min="0" max="180" value={val(r, 'safety_stock_days') ?? ''}
                               onChange={(e) => patch(r.sku_id, 'safety_stock_days', e.target.value)}
                               className="h-7 w-20 px-2 text-right text-[12px] bg-background border border-border rounded font-mono" />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" min="1" value={val(r, 'moq') ?? ''}
                               onChange={(e) => patch(r.sku_id, 'moq', e.target.value)}
                               className="h-7 w-20 px-2 text-right text-[12px] bg-background border border-border rounded font-mono" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </Layout>
  );
}
